import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Sun, Leaf, Wrench, Sprout, Settings, IndianRupee, PlayCircle, CheckCircle2, Zap, Phone, Star, KanbanSquare } from 'lucide-react';
import { COMPANY, STATS, FEATURES, SECTORS, SERVICES, PROCESS, TESTIMONIALS, TRUSTED_LOGOS } from '../mock';

const ICONS = { Leaf, Wrench, Sun, Sprout, Settings, IndianRupee };

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="bg-white">
      <PublicNav />

      {/* HERO */}
      <section id="home" className="relative overflow-hidden bg-gradient-to-b from-orange-50 to-white">
        <div className="absolute inset-0 opacity-40 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(245,158,11,0.12), transparent 40%), radial-gradient(circle at 80% 20%, rgba(16,185,129,0.18), transparent 50%)' }} />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-0 rounded-full px-4 py-1.5">
              <Sun className="w-3.5 h-3.5 mr-1.5 text-amber-500" /> Bangalore’s trusted solar partner
            </Badge>
            <h1 className="mt-6 text-5xl lg:text-6xl font-extrabold leading-[1.05] text-orange-950">
              Design Solar Proposals <span className="relative inline-block">In 20 minutes
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none"><path d="M2 9C70 2 200 2 298 7" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round"/></svg>
              </span> or Less
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl">Inpace Power’s sales platform lets you create polished, accurate solar proposals with shading, financials and BOM — then share with the customer on the spot.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-800 text-white px-7" onClick={() => nav('/login')} data-testid="hero-sales-login">
                Open Sales Tool <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white px-7" onClick={() => nav('/login?next=/app/pipeline')} data-testid="hero-crm">
                <KanbanSquare className="w-4 h-4 mr-2" /> CRM
              </Button>
              <Button size="lg" variant="outline" className="border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100 px-7" onClick={() => nav('/login?role=admin')} data-testid="hero-admin-login">
                Admin Login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="border-orange-300 text-orange-800 hover:bg-orange-50">
                <PlayCircle className="w-4 h-4 mr-2" /> Watch Demo
              </Button>
            </div>
            <div className="mt-10 flex gap-6 text-sm">
              {STATS.slice(0,3).map(s => (
                <div key={s.label}>
                  <div className="text-2xl font-extrabold text-orange-900">{s.value}</div>
                  <div className="text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-amber-200/40 to-orange-300/40 blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-orange-900/20 border-4 border-white float-anim">
              <img src="https://images.unsplash.com/photo-1635424709870-cdc6e64f0e20?w=900&q=85" alt="Solar installation" className="w-full h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-orange-100">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                <Zap className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <div className="text-xs text-slate-500">Yearly Generation</div>
                <div className="text-lg font-bold text-orange-900">7,665 kWh</div>
              </div>
            </div>
            <div className="absolute -top-6 -right-2 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-amber-100">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <div className="text-xs text-slate-500">Yearly Savings</div>
                <div className="text-lg font-bold text-orange-900">₹65,000</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED */}
      <section className="py-10 border-y border-orange-100 bg-white">
        <p className="text-center text-xs font-bold tracking-widest text-orange-600 uppercase">Working with India’s top brands</p>
        <div className="marquee mt-6">
          {[0,1].map(k => (
            <div className="marquee__inner" key={k}>
              {TRUSTED_LOGOS.map((b, i) => (
                <div key={`${k}-${i}`} className="text-2xl font-bold text-slate-300 hover:text-orange-600 transition-colors whitespace-nowrap">{b}</div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="about" className="py-20 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">What We Do</Badge>
          <h2 className="mt-4 text-4xl lg:text-5xl font-extrabold text-orange-950">Complete Renewable Energy Solutions</h2>
          <p className="mt-4 text-slate-600">From rooftop survey to net-metering — Inpace handles everything, end-to-end.</p>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(f => {
            const I = ICONS[f.icon] || Sun;
            return (
              <Card key={f.title} className="p-7 border-orange-100 hover:shadow-xl hover:-translate-y-1 hover:border-orange-200 transition-all bg-gradient-to-br from-white to-orange-50/40">
                <div className="w-12 h-12 rounded-xl bg-orange-600 text-white flex items-center justify-center mb-4">
                  <I className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-orange-950">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* STATS BAND */}
      <section className="py-16 bg-orange-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-extrabold">Our Aim Is To Go Green & Help Nature</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map(s => (
              <div key={s.label} className="text-center p-6 rounded-2xl bg-orange-900/60 border border-orange-800">
                <div className="text-5xl font-extrabold text-amber-400">{s.value}</div>
                <div className="mt-2 text-sm text-orange-200">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" className="py-20 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Sectors We Serve</Badge>
          <h2 className="mt-4 text-4xl lg:text-5xl font-extrabold text-orange-950">Solar for Every Rooftop</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {SECTORS.map(s => (
            <Card key={s.title} className="overflow-hidden border-orange-100 group hover:shadow-2xl hover:-translate-y-1 transition-all">
              <div className="relative h-56 overflow-hidden">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-950/80 to-transparent" />
                <div className="absolute bottom-4 left-5 text-white">
                  <div className="text-4xl font-extrabold">{s.count}</div>
                  <div className="text-sm text-orange-100">{s.label}</div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-orange-950">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
                <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-orange-600">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Across Karnataka
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 bg-orange-50/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">What We Offer</Badge>
              <h2 className="mt-4 text-4xl lg:text-5xl font-extrabold text-orange-950 max-w-2xl">Complete Renewable Solar Solutions</h2>
            </div>
            <Button variant="outline" className="border-orange-300 text-orange-800">View All Services <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map(s => (
              <Card key={s.title} className="overflow-hidden border-orange-100 group hover:shadow-xl transition-all bg-white">
                <div className="h-44 overflow-hidden relative">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <Badge className="absolute top-3 left-3 bg-white text-orange-800 hover:bg-white">Solar Service</Badge>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-orange-950">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
                  <a className="mt-3 inline-flex items-center text-sm font-semibold text-orange-600 hover:text-amber-600">Read More <ArrowRight className="w-3.5 h-3.5 ml-1" /></a>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-20 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Our Process</Badge>
          <h2 className="mt-4 text-4xl lg:text-5xl font-extrabold text-orange-950">How We Power Your Rooftop</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS.map((p, i) => (
            <div key={p.step} className="relative">
              <div className="bg-white border border-orange-100 rounded-2xl p-6 hover:shadow-lg hover:border-orange-300 transition-all h-full">
                <div className="text-6xl font-extrabold text-orange-100">{p.step}</div>
                <h3 className="mt-3 text-lg font-bold text-orange-950">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{p.desc}</p>
              </div>
              {i < PROCESS.length - 1 && (
                <ArrowRight className="hidden lg:block absolute top-1/2 -right-5 w-6 h-6 text-emerald-400" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-orange-50/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Testimonials</Badge>
            <h2 className="mt-4 text-4xl lg:text-5xl font-extrabold text-orange-950">What customers say about us</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Card key={i} className="p-7 border-orange-100 bg-white hover:shadow-xl transition-all">
                <div className="flex gap-0.5">{[0,1,2,3,4].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="mt-4 text-slate-700 leading-relaxed">“{t.text}”</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-500 to-orange-800 flex items-center justify-center text-white font-bold">{t.name[0]}</div>
                  <div>
                    <div className="font-bold text-orange-950">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-600 to-orange-900 px-8 lg:px-16 py-14 text-white">
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-amber-400/20 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-4xl lg:text-5xl font-extrabold">Ready to power your rooftop?</h2>
                <p className="mt-4 text-orange-100">Speak with our Director {COMPANY.director} for a free site survey and an instant proposal.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-orange-950 font-semibold px-7"><Phone className="w-4 h-4 mr-2" /> Call {COMPANY.phone}</Button>
                <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-orange-900" onClick={() => nav('/login')}>Open Sales Tool</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
